import { action, makeObservable, observable } from 'mobx';

import { getUserLanguage } from '../../i18n/helpers';
import { logToNewRelic } from '../../services/debug';
import { resolveFlag } from '../../services/launchDarkly';
import { getErrorMessage } from '../../utils/errors';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import type { MinigameGameType, IframeData, LoaderType } from './types';
import { addPlayerToFrb, postLaunchGame } from './utils';

export class MinigamesStore extends Loadable {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    this.initIframePostMessage();
    makeObservable(this);
  }

  @observable
  public loaderType: LoaderType = 'default';
  @action
  public setLoaderType(loaderType: LoaderType) {
    this.loaderType = loaderType;
  }

  private initIframePostMessage() {
    window.addEventListener('message', (e) => {
      const message = e.data;
      const gameId = message?.payload?.gameId;
      if (message?.type === 'open_minigame' && gameId) {
        void this.startMinigame(
          gameId,
          () => {
            // Notify the iframe that the minigame has been closed
            console.log('Posting minigame_closed message to iframe', e);
            e?.source?.postMessage({ type: 'minigame_closed' }, { targetOrigin: e.origin });
          },
          'curtain',
        );
      }
    });
  }

  /**
   * Handle a `startMinigame` message from a metagame iframe (trail, puzzle, …). Pauses the metagame
   * (`minigameStarted`), opens the minigame, and on close resumes it (`close_minigame`/`minigameEnded`)
   * and refreshes the wallet. One shared implementation so each metagame's switch case is a one-liner:
   *
   *   case 'startMinigame':
   *     minigamesStore.startMetagameMinigame(message.payload?.gameId, iframeRef.current);
   *     break;
   */
  public startMetagameMinigame(gameId: number | string | undefined, iframe: HTMLIFrameElement | null) {
    if (!gameId) {
      console.warn('MinigamesStore startMinigame message missing gameId');
      return;
    }

    const notify = (type: 'minigameStarted' | 'close_minigame' | 'minigameEnded') =>
      iframe?.contentWindow?.postMessage({ type, payload: { gameId } }, '*');

    // Both ended signals are posted so hosts on either contract resume
    // (puzzle/slots: `close_minigame`; trail: `minigameEnded`).
    notify('minigameStarted');
    void this.startMinigame(gameId, () => {
      notify('close_minigame');
      notify('minigameEnded');
      void this.rootStore.walletStore.fetchWallet();
    });
  }

  @observable
  public currentGameType: MinigameGameType | null = null;

  @action
  public setCurrentGameType(gameType: MinigameGameType | null) {
    this.currentGameType = gameType;
  }

  @observable
  public isDialogOpen = false;

  @action
  public setDialogOpen(isOpen: boolean) {
    this.isDialogOpen = isOpen;
  }

  @observable
  public loadingMinigame = false;

  @action
  public setLoadingMinigame(loading: boolean) {
    this.loadingMinigame = loading;
  }

  @observable
  public iframeData: IframeData | null = null;

  @action
  private setIframeData(data: IframeData | null) {
    this.iframeData = data;
  }

  private onCloseListener: VoidFunction | null = null;
  setOnCloseListener(listener: VoidFunction | null) {
    this.onCloseListener = listener;
  }

  public async startMinigame(gameId: string | number, onClose?: VoidFunction, loaderType: LoaderType = 'default') {
    const { userFreeSpinsStore, minigamesWarmStore } = this.rootStore;

    const useWarmFrame = minigamesWarmStore.canLaunch;

    this.setLoaderType(loaderType);
    this.setLoadingMinigame(true);

    if (onClose) {
      this.setOnCloseListener(onClose);
    }

    if (!useWarmFrame) {
      this.setDialogOpen(true);
    }

    // If it's from widget click
    const isFromFreeSpinWidget = Boolean(userFreeSpinsStore.selectedFreeSpin);
    if (!isFromFreeSpinWidget) {
      void userFreeSpinsStore.fetch();
    }
    // Register the FRB and fetch the launch URL in parallel — they're independent,
    // so this shaves one network round-trip off the time-to-open.
    const shouldAddFrb =
      Boolean(userFreeSpinsStore.selectedFreeSpin?.bonusCode) && resolveFlag('fs_widget_add_player_frb', true);
    const [, gameData] = await Promise.all([
      shouldAddFrb ? addPlayerToFrb(userFreeSpinsStore.selectedFreeSpin!.bonusCode) : Promise.resolve(),
      this.launchGame(gameId),
    ]);

    if (gameData) {
      const gameUrl = gameData.slotGameUrl || gameData.gameUrl;
      const url = new URL(gameUrl);
      // `disabledSounds` is the third-party mini-game provider's own param (added in #1656); game-ui
      // reads `soundEnabled` instead and ignores that one, so both go out — a mini-game is served by
      // either host depending on the title.
      url.searchParams.set('disabledSounds', String(this.rootStore.uiStore.disabledSounds));
      url.searchParams.set('soundEnabled', String(!this.rootStore.uiStore.disabledSounds));
      url.searchParams.set('language', getUserLanguage(this.rootStore.userStore.profile?.language));

      if (useWarmFrame && minigamesWarmStore.launch(url.toString())) {
        this.setLoadingMinigame(false);

        return;
      }

      if (useWarmFrame) {
        this.setDialogOpen(true);
      }

      this.setIframeData({ src: url.toString() });
    } else {
      this.closeMinigame();
    }
    this.setLoadingMinigame(false);
  }

  public closeMinigame(withError = false) {
    this.rootStore.minigamesWarmStore.close();
    this.setIframeData(null);
    this.setDialogOpen(false);
    this.onCloseListener?.();
    this.setOnCloseListener(null);
    this.setError('');

    if (!withError) {
      void this.rootStore.userFreeSpinsStore.fetch();
    }
  }

  public async launchGame(gameId: string | number) {
    const { coinType } = this.rootStore.coinTypeStore;

    if (this.loading) return;
    this.setLoading(true);

    try {
      const launchGameData = await postLaunchGame({ gameId, coin: coinType });
      this.setLoading(false);
      return launchGameData;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      void logToNewRelic(
        'Error launching game FE',
        'Error launching game FE: ' + JSON.stringify({ gameId, coinType, errorMessage }),
        'error',
      );
    }

    this.setLoading(false);
  }
}
